import React, { useState, useEffect, useContext } from "react";
import Bugsnag from "@bugsnag/browser";
import { TextField, CircularProgress } from "@mui/material";
import { createPayment } from "../../services/paymentService";
import { processErrorResponse } from "../../helpers/processErrorResponse";
import { Select } from "../../ui/index";
import FileUploader from "../shared/FileUploader";
import { useDispatch, useSelector } from "react-redux";
import Modal from "react-modal";
import { ValidatePaymentAmmount } from "../shared/regex";
import { getPassengerById } from "../../services/passengerService";
import PaymentPlanTable from "./PaymentPlanTable";
import { AbitabButton } from "./AbitabButton";
import { OverlayNotification } from "../OverlayNotification";
import { getUserPaymentDetails } from "../../services/userService";
import PaymentFeesSelector from "./PaymentFeesSelector";
import { toggleOverlay } from "../../store/actions/notificationActions";
import { CenteredLoader } from "../shared/CenteredLoader";
import useMobile from "../../hooks/useOrientation";
import { primary } from "../../ui/colors";
import { useNavigate } from "react-router-dom";
import { getSessionCookie, SessionContext } from "../../helpers/session";
import { jwtDecode } from "jwt-decode";
import { getCurrencySymbol } from "../../helpers/currency";
import { shouldHidePaymentPlan } from "../../config/registrationMessages";

const primaryDark = "#00a896";
const primaryLight = "#e0faf7";

const paymentSentLabel =
  "Tu pago ha sido correctamente enviado y está pendiente de aprobación hasta que la administración compruebe que la trasferencia ha sido acreditada.";
const paymentSentLabelScreen =
  "Pago enviado. Deberá ser aprobado por administración antes de acreditarse en la cuenta corriente. Ir a Mis Viajes para ver siguientes pagos.";

const AddPayment = () => {
  const { user } = useContext(SessionContext);
  const { showNotification } = useSelector((state) => state.notification);
  const navigate = useNavigate();
  const { id } = user;

  const [paymentData, setPaymentData] = useState({
    ammount: "",
    file: "",
    currency: "",
    passengerId: null,
    fileType: "",
  });

  const [passenger, setPassenger] = useState();
  const hidePaymentPlan = shouldHidePaymentPlan({
    programId: passenger?.programId,
    programName: passenger?.programName,
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [paymentSent, setPaymentSent] = useState(false);
  const [loading, setLoading] = useState();
  const [error, setError] = useState();
  const [uploadedFileType, setUploadedFileType] = useState();
  const storePassengers = useSelector((state) => state.passengers);
  const [passengers, setPassengers] = useState();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [paymentFees, setPaymentFees] = useState();
  const [manualPaymentEnabled, setManualPaymentEnabled] = useState(false);
  const [manualPaymentMode, setManualPaymentMode] = useState(false);
  const isMobile = useMobile();
  const dispatch = useDispatch();

  const submitPayment = () => {
    setLoading(true);
    if (!paymentData.passengerId) {
      setError("Elegir pasajero para enviar un pago.");
      setLoading(false);
    } else if (!ValidatePaymentAmmount(paymentData.ammount)) {
      setError("Monto invalido.");
      setLoading(false);
    } else {
      createPayment(id, paymentData.ammount, paymentData)
        .then((res) => {
          setPaymentSent(true);
          dispatch(
            toggleOverlay({
              show: true,
              text: paymentSentLabel,
              success: true,
            }),
          );
          setPassenger(null);
          setPaymentData({ ammount: "", file: "", passengerId: "" });
          setError("");
          setManualPaymentMode(false);
          setLoading(false);
          Bugsnag.notify("Envio de pago.", function (event) {
            event.severity = "info";
            const { UserID, Name, LastName, Email } = jwtDecode(
              getSessionCookie(),
            );
            event.setUser(UserID, Email, Name + " " + LastName);
            event.addMetadata("paymentData", paymentData);
          });
        })
        .catch((e) => {
          processErrorResponse(e);
          setLoading(false);
          setError(processErrorResponse(e)[1]);
        });
    }
  };

  const convertPassengersToOptions = () =>
    storePassengers.passengers.map((p) => ({
      key: p.id,
      label: p.name + " - Viaje: " + p.tripName,
    }));

  const handleFileUpload = (file, type) => {
    if (type > -1) {
      setPaymentData({ ...paymentData, file, fileType: type });
      setUploadedFileType(type);
      setImageLoaded(true);
    } else {
      Bugsnag.notify(
        new Error("Intento subir una imagen con formato invalido."),
      );
      setError("El comprobante de pago debe ser una imagen o un PDF.");
    }
  };

  const handleClickSeeFile = () => window.open(paymentData.file);

  useEffect(() => {
    setPassengers(storePassengers.passengers);
    const disableManualPayments = storePassengers?.passengers?.find(
      (p) =>
        p.programName?.includes("2026") ||
        p.programName?.includes("2027") ||
        p.programName?.includes("2028"),
    );
    setManualPaymentEnabled(!disableManualPayments);
    if (storePassengers?.passengers?.length === 1) {
      const pass = storePassengers.passengers[0];
      setPaymentData({ ...paymentData, passengerId: pass.id });
      getPassengerById(pass.id).then((res) => {
        setPassenger(res);
        setPaymentData((prev) => ({ ...prev, currency: res.currency }));
        getUserPaymentDetails(res.documentId).then((pd) =>
          setPaymentFees(pd.payments),
        );
      });
    }
  }, [storePassengers]);

  const handleChangePassenger = (e) => {
    setPaymentFees(null);
    setPaymentSent(false);
    setPaymentData({ ...paymentData, passengerId: e.target.value });
    getPassengerById(e.target.value).then((res) => {
      setPassenger(res);
      setPaymentData((prev) => ({ ...prev, currency: res.currency }));
      getUserPaymentDetails(res.documentId).then((pd) =>
        setPaymentFees(pd.payments),
      );
    });
  };

  const handleChangeSelectedFees = (value) => {
    setPaymentData({ ...paymentData, ammount: value });
  };

  const storePass = storePassengers?.passengers;
  const addPaymentLabel =
    storePass?.length > 0
      ? `Ingresar pago para el viaje de ${storePass[0].name} ${storePass[0].lastName} a ${storePass[0].programName}`
      : null;

  if (!passengers?.length || loading) {
    return (
      <div style={styles.loaderWrapper}>
        <CircularProgress style={{ color: primary }} />
      </div>
    );
  }

  return (
    <>
      <OverlayNotification />
      <div
        style={{
          ...styles.container,
          padding: isMobile ? "16px 12px" : "24px 20px",
          display: showNotification ? "none" : "flex",
        }}
      >
        {/* Page title */}
        <h1 style={styles.pageTitle}>Pagos</h1>

        {/* Payment method cards */}
        <div
          style={{
            ...styles.methodsGrid,
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          }}
        >
          {/* Abitab card */}
          <div style={styles.methodCard}>
            <AbitabButton abitab disabled={!manualPaymentEnabled} />
            <p style={{ ...styles.methodDescription, fontSize: 14 }}>
              Pagá en cualquier local de Abitab con la cédula del pasajero. Una
              vez acreditado recibirás el recibo por mail.
            </p>
          </div>

          {/* Transferencia card */}
          {manualPaymentEnabled && (
            <div
              style={{
                ...styles.methodCard,
                ...(manualPaymentMode ? styles.methodCardActive : {}),
                cursor: "pointer",
              }}
              onClick={() => !manualPaymentMode && setManualPaymentMode(true)}
            >
              <AbitabButton onPressButton={() => setManualPaymentMode(true)} />
              <p style={styles.methodDescription}>
                Subí el comprobante de tu transferencia bancaria.
              </p>
            </div>
          )}
        </div>

        {/* Manual payment form */}
        {manualPaymentMode && (
          <div style={styles.formCard}>
            {/* Passenger selector */}
            {storePassengers?.passengers.length > 1 ? (
              <div style={styles.formSection}>
                <label style={styles.formLabel}>Pasajero</label>
                <Select
                  onChange={(e) => handleChangePassenger(e)}
                  placeholder="Elegir pasajero/a"
                  options={convertPassengersToOptions()}
                  customStyles={{ width: "100%" }}
                />
              </div>
            ) : (
              <div style={styles.infoRow}>
                <span style={styles.formLabel}>Pasajero</span>
                <span style={styles.infoValue}>{addPaymentLabel}</span>
              </div>
            )}

            {/* Plan de pagos */}
            {paymentFees?.length === 0 ? (
              <div style={styles.successBanner}>
                ✅ Tu plan de pagos ha sido completado.
              </div>
            ) : !passenger ? null : paymentFees?.length > 0 ? (
              <>
                {!hidePaymentPlan && (
                  <div style={styles.formSection}>
                    <label style={styles.formLabel}>Próximos pagos</label>
                    <PaymentFeesSelector
                      paymentFees={paymentFees}
                      onChangeFees={handleChangeSelectedFees}
                      currency={passenger.currency}
                    />
                  </div>
                )}

                {/* Amount + currency */}
                <div style={styles.formSection}>
                  <label style={styles.formLabel}>Monto a pagar</label>
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <TextField
                      type="number"
                      variant="outlined"
                      size="small"
                      style={{ width: 160 }}
                      onChange={(e) =>
                        setPaymentData({
                          ...paymentData,
                          ammount: e.target.value,
                        })
                      }
                      placeholder="Monto"
                      value={paymentData.ammount}
                    />
                    <span style={styles.currencyLabel}>
                      {getCurrencySymbol(passenger?.currency)}
                    </span>
                  </div>
                  <span style={styles.hint}>
                    Asegurate que el monto coincide con el comprobante.
                  </span>
                </div>

                {/* File uploader */}
                <div style={styles.formSection}>
                  <label style={styles.formLabel}>Comprobante de pago</label>
                  <FileUploader onFileUploaded={handleFileUpload} />
                  {imageLoaded && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginTop: 8,
                      }}
                    >
                      <span style={styles.successText}>
                        {uploadedFileType === 0
                          ? "✅ Imagen cargada."
                          : "✅ PDF cargado."}
                      </span>
                      <button
                        style={styles.linkBtn}
                        onClick={handleClickSeeFile}
                      >
                        Ver archivo
                      </button>
                    </div>
                  )}
                </div>

                {/* Submit */}
                <button
                  style={{
                    ...styles.submitBtn,
                    opacity: !imageLoaded || paymentData.ammount < 10 ? 0.5 : 1,
                    cursor:
                      !imageLoaded || paymentData.ammount < 10
                        ? "not-allowed"
                        : "pointer",
                  }}
                  disabled={loading || !imageLoaded || paymentData.ammount < 10}
                  onClick={submitPayment}
                >
                  Enviar pago
                </button>
              </>
            ) : (
              <CenteredLoader />
            )}
          </div>
        )}

        {/* Payment sent confirmation */}
        {paymentSent && (
          <div style={styles.sentCard}>
            <p style={styles.sentText}>{paymentSentLabelScreen}</p>
            <button
              style={styles.submitBtn}
              onClick={() => navigate("/misViajes")}
            >
              Ir a Mis Viajes
            </button>
          </div>
        )}

        {/* Error */}
        {error && <div style={styles.errorBanner}>{error}</div>}
      </div>

      <Modal
        contentLabel="Comprobante"
        isOpen={isModalOpen}
        ariaHideApp={false}
        style={{
          content: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        <button style={styles.linkBtn} onClick={() => setIsModalOpen(false)}>
          Cerrar
        </button>
        <img
          style={{ width: 500, height: "auto" }}
          src={paymentData.file}
          alt=""
        />
        {passenger && !hidePaymentPlan && (
          <PaymentPlanTable passenger={passenger} />
        )}
      </Modal>
    </>
  );
};

export default AddPayment;

// ---- Styles ----

const styles = {
  container: {
    maxWidth: 720,
    width: "100%",
    margin: "0 auto",
    flexDirection: "column",
  },
  loaderWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    width: "100%",
    maxWidth: 720,
    margin: "0 auto",
    boxSizing: "border-box",
  },
  pageTitle: {
    fontSize: 26,
    fontWeight: 700,
    color: "#111",
    fontFamily: "Gilroy-Bold",
    marginBottom: 24,
  },
  methodsGrid: {
    display: "grid",
    gap: 16,
    marginBottom: 24,
  },
  methodCard: {
    border: "1.5px solid #e0e0e0",
    borderRadius: 12,
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: 12,
    transition: "border-color 0.15s",
  },
  methodCardActive: {
    borderColor: primary,
    background: primaryLight,
  },
  methodDescription: {
    fontSize: 12,
    color: "#666",
    lineHeight: 1.5,
  },
  formCard: {
    border: "1.5px solid #e0e0e0",
    borderRadius: 12,
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    gap: 20,
    marginBottom: 20,
  },
  formSection: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  formLabel: {
    fontSize: 12,
    color: "#888",
    fontWeight: 500,
  },
  infoRow: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "12px 0",
    borderBottom: "1px solid #f0f0f0",
  },
  infoValue: {
    fontSize: 12,
    fontWeight: 600,
    color: "#111",
  },
  currencyLabel: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 64,
    height: 40,
    padding: "0 16px",
    border: "1.5px solid #e0e0e0",
    borderRadius: 8,
    color: "#444",
    fontSize: 14,
    fontWeight: 600,
    background: "#fafafa",
    boxSizing: "border-box",
  },
  hint: {
    fontSize: 12,
    color: "#999",
    marginTop: 4,
  },
  successText: {
    fontSize: 12,
    color: primaryDark,
    fontWeight: 500,
  },
  linkBtn: {
    background: "none",
    border: "none",
    color: primaryDark,
    fontWeight: 600,
    fontSize: 12,
    cursor: "pointer",
    padding: 0,
    textDecoration: "underline",
  },
  submitBtn: {
    padding: "11px 24px",
    background: primary,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 700,
    fontSize: 12,
    cursor: "pointer",
    alignSelf: "flex-start",
    fontFamily: "Gilroy",
    transition: "opacity 0.15s",
  },
  sentCard: {
    border: "1.5px solid #e0e0e0",
    borderRadius: 12,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  sentText: {
    fontSize: 12,
    color: "#444",
    lineHeight: 1.6,
  },
  errorBanner: {
    background: "#fdecea",
    border: "1px solid #f5c6c6",
    borderRadius: 8,
    padding: "10px 16px",
    color: "#c0392b",
    fontSize: 12,
    marginTop: 8,
  },
  successBanner: {
    background: primaryLight,
    border: `1px solid ${primary}`,
    borderRadius: 8,
    padding: "10px 16px",
    color: primaryDark,
    fontSize: 12,
  },
};
